import React, {useEffect, useState} from 'react'
import {Form, Input, InputNumber, Select, Spin} from 'antd'
import {useNavigate, useParams} from 'react-router-dom'
import {useAppContext} from 'common/context/AppContext.tsx'
import {TrackerBean} from 'common/api/api.ts'
import {ButtonsPanel} from 'common/components/ButtonsPanel.tsx'
import {apiCall} from 'common/util/Util.tsx'
import {FormCard} from 'common/components/FormCard.tsx'
import {trackerApi, trackerEventApi} from 'common/api/entitiesApi.tsx'
import {AdvertiserSelector} from 'common/components/reloadable/AdvertiserSelector.tsx'
import {CommonRoutes} from 'common/context/commonRoutes.ts'
import {AnchorsForm} from 'common/components/AnchorsForm.tsx'
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import {Option} from "antd/es/mentions";
import {EventSelector} from "common/components/reloadable/EventSelector.tsx";
import TrackerTypeEnum = TrackerBean.TrackerTypeEnum;

const FormItem = Form.Item

type Mode = 'create' | 'edit';

interface TrackerFormProps {
    mode: Mode;
    agency: boolean;
}

const TrackerForm: React.FC<TrackerFormProps> = ({mode, agency}) => {
    const {id} = useParams<{ id?: string }>()
    const idNum = id ? parseInt(id) : 0

    const navigateTo = useNavigate()
    const appContext = useAppContext()

    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)

    const [withPrice, setWithPrice] = useState(false)
    const trackerType = Form.useWatch('trackerType', form)

    const initialTrackerBean = {trackerType: TrackerTypeEnum.Postback} as TrackerBean
    const [entity, setEntity] = useState<TrackerBean>(initialTrackerBean)

    const loadData = () => {
        if (mode === 'edit') {
            apiCall(
                setLoading,
                appContext,
                () => {
                    return trackerApi.viewTracker(idNum)
                },
                (res) => {
                    if (res && res.data) {
                        setEntity(res.data)
                        form.setFieldsValue({...res.data})
                    }
                }
            )
        }
        if (mode === 'create') {
            setEntity(initialTrackerBean)
            form.setFieldsValue({...initialTrackerBean})
        }
    }

    useEffect(() => {
        loadData()
    }, [])

    const onDelete = () => {
        apiCall(setLoading, appContext, () => trackerApi.deleteTracker(idNum), () => navigateTo(CommonRoutes.TRACKER))
    }

    const onCancel = () => navigateTo(CommonRoutes.TRACKER)

    const handleSubmit = () => {
        form.validateFields().then(values => {
            setLoading(true)
            apiCall(
                setLoading,
                appContext,
                () => {
                    if (mode === 'edit' && idNum) {
                        return trackerApi.updateTracker({...values}, idNum)
                    } else {
                        return trackerApi.createTracker({...values})
                    }
                },
                (response) => {
                    queueListRepositionOnModifiedChange({
                        appContext,
                        routeKey: CommonRoutes.TRACKER,
                        previousEntity: entity,
                        responseEntity: response?.data,
                        enabled: mode === 'edit'
                    })

                    navigateTo(CommonRoutes.TRACKER)
                }
            )
        })
    }

    return (
        <Spin spinning={loading}>
            <AnchorsForm form={form} onFinish={handleSubmit}>
                <FormCard title="Tracker">
                    <FormItem name="eventType" hidden/>

                    {agency && <FormItem
                        name="advertiserId"
                        label="Advertiser"
                        tooltip="Select the advertiser that this tracker is associated with."
                        rules={[{required: true, message: 'Advertiser is required'}]}
                    >
                        <AdvertiserSelector placeholder="Select advertiser" disabled={mode === 'edit'}/>
                    </FormItem>}

                    <FormItem
                        name="name"
                        label="Name"
                        rules={[{required: true, message: 'Please input Tracker Name'}]}
                    >
                        <Input maxLength={255}/>
                    </FormItem>

                    {mode == 'edit' && <FormItem label="Token" tooltip="Unique identifier used to track and attribute conversions or events." name="token">
                        {form.getFieldValue('token')}
                    </FormItem>}

                    <FormItem
                        name="trackerType"
                        label="Type"
                        tooltip="Select the type of tracker — use Postback/Pixel to track conversions via server-to-server calls or image pixels."
                        required
                        rules={[{required: true, message: 'Type is required'}]}
                    >
                        <Select
                            allowClear
                            placeholder="Select type"
                            showSearch
                            optionFilterProp="children"
                            style={{width: 200}}
                            disabled={mode === 'edit'}
                        >
                            <Option value={TrackerTypeEnum.Postback.toString()}>Postback</Option>
                            <Option value={TrackerTypeEnum.Pixel.toString()}>Pixel</Option>
                        </Select>
                    </FormItem>

                    {trackerType == TrackerTypeEnum.Postback &&
                        <FormItem label="Event" name="eventId" required>
                            <EventSelector disabled={mode === 'edit'}
                                           style={{width: 200}} onChange={id => {
                                if (id) {
                                    apiCall(
                                        setLoading,
                                        appContext,
                                        () => {
                                            return trackerEventApi.trackerEvent(id as number)
                                        },
                                        (res) => {
                                            if (res && res.data) {
                                                setWithPrice(res.data.withPrice == true)
                                            }
                                        }
                                    )
                                } else {
                                    setWithPrice(false)
                                }
                            }}/>
                        </FormItem>
                    }

                    {trackerType == TrackerTypeEnum.Postback && withPrice &&
                        <FormItem
                            name="cost"
                            label="Default Price"
                            tooltip="Set the monetary value associated with this tracker (e.g., payout or cost)."
                            required
                        >
                            <InputNumber
                                suffix="$"
                                step={0.01}
                                min={0}
                                style={{width: 100}}
                            />
                        </FormItem>
                    }
                </FormCard>

                <ButtonsPanel onCancel={onCancel} onDelete={onDelete} showDelete={mode === 'edit'}/>
            </AnchorsForm>
        </Spin>
    )
}

export default TrackerForm
